/**
 * The Step 1 schedule-source chooser: selectable cards (Upload PDF / Upload
 * image / Paste a link). Presentational; the parent owns which source is
 * selected and who is allowed to see the link card.
 */

import { tourAnchor } from '../../../../tours/tourAnchors';

export type ScheduleSource = 'pdf' | 'image' | 'link';

interface SourceOption {
  id: ScheduleSource;
  icon: string;
  title: string;
  detail: string;
}

const OPTIONS: readonly SourceOption[] = [
  {
    id: 'pdf',
    icon: '📄',
    title: 'Upload PDF',
    detail: 'Upload a PDF of the schedule.',
  },
  {
    id: 'image',
    icon: '🖼',
    title: 'Upload image',
    detail: 'Upload a screenshot of the schedule.',
  },
  {
    id: 'link',
    icon: '🔗',
    title: 'Paste a link',
    detail: 'Read the schedule straight off a tournament site.',
  },
];

interface SourceChooserProps {
  selected: ScheduleSource;
  onSelect: (source: ScheduleSource) => void;
  /**
   * Show the "Paste a link" card. False for everyone but a system admin.
   *
   * Hiding it is presentation only — the API answers 403 to a rendered fetch from
   * anyone else, which is the check that actually matters.
   */
  allowLink?: boolean;
}

export function SourceChooser({
  selected,
  onSelect,
  allowLink = false,
}: SourceChooserProps) {
  const options = OPTIONS.filter(
    (option) => option.id !== 'link' || allowLink
  );

  return (
    // A plain toggle-button group, not an ARIA radiogroup: the radiogroup role
    // promises arrow-key roving between options, which we do not implement.
    // aria-pressed on toggle buttons is the honest, complete semantics here
    // (mirrors the ScheduleIngestionModal source toggle).
    <div
      className="wiz-sources"
      role="group"
      aria-label="Schedule source"
      {...tourAnchor('build-itinerary.event.source')}
    >
      {options.map((option) => {
        const isSelected = option.id === selected;
        return (
          <button
            type="button"
            key={option.id}
            aria-pressed={isSelected}
            className={`wiz-source${isSelected ? ' wiz-source--selected' : ''}`}
            onClick={() => onSelect(option.id)}
            data-testid={`wizard-source-${option.id}`}
          >
            <b className="wiz-source__title">
              <span aria-hidden="true">{option.icon}</span> {option.title}
            </b>
            <small className="wiz-source__detail">{option.detail}</small>
          </button>
        );
      })}
    </div>
  );
}
